// admin/src/pages/ChangePasswordPage.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { changeAdminPassword } from '../services/api';

type FieldErrors = {
  currentPassword?: string;
  newPassword?: string;
  newPassword2?: string;
};

const ChangePasswordPage: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showNew2, setShowNew2] = useState(false);

  const validate = () => {
    const e: FieldErrors = {};

    if (!currentPassword.trim()) e.currentPassword = 'Current password is required';

    if (!newPassword.trim()) e.newPassword = 'New password is required';
    else if (newPassword.length < 8)
      e.newPassword = 'New password must be at least 8 characters';

    if (!newPassword2.trim()) e.newPassword2 = 'Please confirm new password';
    else if (newPassword && newPassword2 && newPassword !== newPassword2)
      e.newPassword2 = 'Passwords do not match';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await changeAdminPassword({ currentPassword, newPassword });
      Swal.fire('Success', 'Password updated successfully', 'success');
      setCurrentPassword('');
      setNewPassword('');
      setNewPassword2('');
      setErrors({});
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to change password';
      Swal.fire('Error', msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Typography variant="h5" sx={{ mt: 2, mb: 2 }}>
        Change Password
      </Typography>

      {/* Full-width form area */}
      <Paper sx={{ p: { xs: 2, md: 3 }, width: '100%' }}>
        <Box
          component="form"
          noValidate
          autoComplete="off"
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 2,
            width: '100%',
          }}
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {/* Current Password */}
          <TextField
            label="Current Password"
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => {
              setCurrentPassword(e.target.value);
              if (errors.currentPassword) setErrors({ ...errors, currentPassword: undefined });
            }}
            error={!!errors.currentPassword}
            helperText={errors.currentPassword}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle current password visibility"
                    onClick={() => setShowCurrent((s) => !s)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showCurrent ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* New Password */}
          <TextField
            label="New Password"
            type={showNew ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (errors.newPassword) setErrors({ ...errors, newPassword: undefined });
            }}
            error={!!errors.newPassword}
            helperText={errors.newPassword || 'At least 8 characters'}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle new password visibility"
                    onClick={() => setShowNew((s) => !s)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Confirm New Password */}
          <TextField
            label="Confirm New Password"
            type={showNew2 ? 'text' : 'password'}
            value={newPassword2}
            onChange={(e) => {
              setNewPassword2(e.target.value);
              if (errors.newPassword2) setErrors({ ...errors, newPassword2: undefined });
            }}
            error={!!errors.newPassword2}
            helperText={errors.newPassword2}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowNew2((s) => !s)}
                    edge="end"
                    tabIndex={-1}
                  >
                    {showNew2 ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Box display="flex" justifyContent="flex-end" mt={1} gap={2}>
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={saving}
              sx={{
                minWidth: 180,
                background: 'linear-gradient(to right, #6226A7, #F6A938)',
                color: '#fff',
                fontWeight: 600,
                '&:hover': { background: 'linear-gradient(to right, #4e1f84, #db8b21)' },
              }}
            >
              {saving ? 'Updating…' : 'Update Password'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default ChangePasswordPage;
